import { chain, chunk, compact, entries, filter, find, findKey, flatMap, flatten, forEach, get, has, includes, intersection, isArray, isEmpty, isNil, isNumber, isObject, keyBy, keys, map, mapKeys, mapValues, mean, orderBy, pickBy, reduce, some, sortBy, sumBy, values } from "lodash";
import { Moment } from "moment";
import foodIntolerances from "../files/foodIntolerances.json";
import { LineItem, ProductCategoryId, ProductGroupHierachy, SelectOption, ServiceSurcharge, TransactionSurcharge, TreeData, VoucherProduct } from "../types";
import { Booking } from "../types/Booking";
import { FunctionBooking } from "../types/FunctionBooking";
import { GroupHeadingProductSizes, GroupHeadings, Menu, PaymentRequirementTypes } from "../types/Menu";
import { OrderItem, OrderItemProducts, OrderModifier, OrderModifiers } from "../types/Order";
import { Adjustment, AdjustmentProductIds, Adjustments, OpeningTimes } from "../types/Seating";
import { AdditionGroup, AdditionGroups, ComboGroup, EnrichedProduct, EnrichedProductMetaData, FoodIntolerance, FoodIntolerances, GroupHeadingHierachy, Modifier, ModifierGroups, PreparationGroup, PreparationGroups, PriceOption, PriceOptions, Product, ProductAddition, ProductEnhancementType, ProductGroup, ProductGroups, ProductInfo, ProductModifier, ProductPreparation, ProductPriceInfo, ProductSearchResult, ProductSize, ProductSizes, ProductStock, ProductTypeId, Products, SizeIdMap, SpecialProduct, UpsellGroups } from "../types/product";
import { canThisBeNumber, getMenuItem, getOpeningTimesAdjusted, getTicks } from "./../utils";
import { MoneyUtils } from "./MoneyUtils";
import { isStatusEqualByBooking } from "./bookingUtils";
import { getLineItemProductId, getLineItemSizeId, getTaxAmount } from "./transactionUtils";

function validateComboGroup(products: Product[], comboGroup: ComboGroup, values: OrderItem, restaurantId: string, modifierGroups: Modifier[], additionGroups: AdditionGroup[], preparationGroups: PreparationGroup[], errors: any, index: number) {
  if (!values) return;
  let comboCount = 0;
  if (comboGroup.includeAllCombinations) return;

  for (const productId in comboGroup.combinations) {
    const enhancementErrors: any = { modifiers: {}, additions: {}, preparations: {} };
    const comboProduct = find(products, (p) => p.id === productId);
    if (values.comboGroup?.[productId]?.quantity) {
      comboCount += Number(values.comboGroup[productId].quantity);
    }
    // Validate enhancements within this ComboGroupProduct
    if (comboProduct) {
      const { modifiers, additions, preparations } = comboProduct;
      forEach(modifiers, (modifier) => validateProductModifier(modifier, values.comboGroup?.[productId], restaurantId, modifierGroups, enhancementErrors));
      forEach(additions, (addition) => validateProductAddition(addition, values.comboGroup?.[productId], restaurantId, additionGroups, enhancementErrors));
      forEach(preparations, (preparation) => validateProductPreparation(preparation, values.comboGroup?.[productId], restaurantId, preparationGroups, enhancementErrors));
      errors.nestedEnhancements[productId] = enhancementErrors;
    }
  }

  const maxAllowedWQuantity = values.isComboQuantityIndependentFromOrderItem ? Number(comboGroup.quantity) * values.quantity : Number(comboGroup.quantity);
  if (comboCount < maxAllowedWQuantity) {
    errors.comboGroup[index] = "Required";
  } else if (comboCount > maxAllowedWQuantity) {
    errors.comboGroup[index] = "Cannot Exceed Limit";
  }
}

function validateProductModifier(modifier: ProductModifier, values: OrderItem, restaurantId: string, modifierGroups: Modifier[], errors: any) {
  if (!values) return;
  const mg = find(modifierGroups, (mg) => mg.id === modifier.modifierGroupId && mg.restaurants[restaurantId]);
  if (!mg) return;
  if (modifier.mandatory && !values.modifiers?.[modifier.modifierGroupId]) {
    errors.modifiers[modifier.modifierGroupId] = "Required";
  }
}

function validateProductAddition(addition: ProductAddition, values: OrderItem, restaurantId: string, additionGroups: AdditionGroup[], errors: any) {
  if (!values) return;
  let additionCount = 0;
  const ag = find(additionGroups, (ag) => ag.id === addition.additionGroupId && ag.restaurants[restaurantId]);
  if (!ag) return;
  for (const additionProductId in values.additions?.[addition.additionGroupId] || {}) {
    if (values.additions[addition.additionGroupId][additionProductId].quantity > 0) {
      additionCount += 1;
    }
  }

  if (addition.mandatory && additionCount < 1) {
    errors.additions[addition.additionGroupId] = "Required";
  } else if (addition.limitedSelection && additionCount > Number(addition.totalselections)) {
    errors.additions[addition.additionGroupId] = "Cannot Exceed Limit";
  }
}

function validateProductPreparation(preparation: ProductPreparation, values: OrderItem, restaurantId: string, preparationGroups: PreparationGroup[], errors: any) {
  if (!values) return;
  const pg = find(preparationGroups, (pg) => pg.id === preparation.preparationGroupId && pg.restaurants[restaurantId]);
  if (!pg) return;
  const selectedSizeHasPreparation = (preparation.sizes && values.sizeId && preparation.sizes[values.sizeId]) || false;
  const preparationCount = values.preparations?.[preparation.preparationGroupId]?.length || 0;

  if (preparation.mandatory && selectedSizeHasPreparation && preparationCount < 1) {
    errors.preparations[preparation.preparationGroupId] = "Required";
  } else if (preparation.limitedSelection && selectedSizeHasPreparation && preparationCount > Number(preparation.totalselections)) {
    errors.preparations[preparation.preparationGroupId] = "Cannot Exceed Limit";
  }
}

export function validateProductPageModal(orderItem: OrderItem, product: Product, restaurantId: string, modifierGroups: Modifier[], additionGroups: AdditionGroup[], preparationGroups: PreparationGroup[], products?: Product[]) {
  const errors: any = { comboGroup: [], modifiers: {}, additions: {}, preparations: {}, nestedEnhancements: {} };
  if (isOpenProduct(product, restaurantId) && !isNoSaleProduct(product, restaurantId, orderItem.sizeId)) {
    if (!orderItem.manualAmount) {
      errors.manualAmount = "Required";
    } else if (Number(orderItem.manualAmount <= 0)) {
      errors.manualAmount = "Must be greater than zero";
    }
  }

  if (!orderItem.sizeId) errors.sizeId = "Please select a size";
  forEach(product.groupProducts?.[orderItem.sizeId], (comboGroup, index) => validateComboGroup(products, comboGroup, orderItem, restaurantId, modifierGroups, additionGroups, preparationGroups, errors, index));
  forEach(product.modifiers, (modifier) => validateProductModifier(modifier, orderItem, restaurantId, modifierGroups, errors));
  forEach(product.additions, (addition) => validateProductAddition(addition, orderItem, restaurantId, additionGroups, errors));
  forEach(product.preparations, (preparation) => validateProductPreparation(preparation, orderItem, restaurantId, preparationGroups, errors));

  return errors;
}

export function getAdjustment(adjustments: Adjustments, menuId: string, now: Moment, booking: Booking | FunctionBooking, openingTimes: OpeningTimes): Adjustment[] {
  if (!openingTimes) return [];
  let ticks = 0;
  if (!booking || isStatusEqualByBooking(booking, "arrived", "seated")) {
    ticks = getTicks(now);
  } else {
    ticks = Number(booking.intervalId);
  }
  // conditionally add 24 hours if arrived or seated past booking day
  if (booking && isStatusEqualByBooking(booking, "arrived", "seated") && booking.date && Number(now.format("YYYYMMDD")) > booking.date) ticks += 24 * 60 * 60 * 1000; // add 24 hours
  return values(
    pickBy(adjustments, (a) => {
      const { open: from1, close: to1 } = getOpeningTimesAdjusted(a.from, a.to);
      return a.menuId === menuId && ticks >= Number(from1) && ticks <= Number(to1);
    })
  );
}

export function shouldShowWithStandardPrice(adjustments: Adjustments, menuId: string, now: Moment, booking: Booking | FunctionBooking, openingTimes: OpeningTimes, priceOptions: PriceOptions): boolean {
  const applicableAdjustments = getAdjustment(adjustments, menuId, now, booking, openingTimes);

  return some(applicableAdjustments, ({ priceOptionId }) => priceOptions[priceOptionId]?.showWithStandardPrice);
}

export const canApplyAdjustmentToProduct = (productId: string, _sizeId: string, priceOption: PriceOption) => {
  if (!productId || !priceOption) return false;
  const sizeId = _sizeId || "";
  const productAdjustments = priceOption.adjustmentProductIds?.[productId];

  // SaleProducts carry a real size on the line item -> match that size exactly.
  // Menus / fees have no size on the line item (sizeId === "") -> match if the product
  // is enabled under ANY stored size key (e.g. "adult"/"child", or "" or "noSize").
  const isEnabledForProduct = sizeId ? !!productAdjustments?.[sizeId]?.enabled : some(productAdjustments, (opt) => opt?.enabled);

  // applyToAllProducts === true: listed products are EXCLUSIONS
  if (priceOption.applyToAllProducts === true) return !isEnabledForProduct;
  return isEnabledForProduct;
};
/**
 * Returns unit price information along with applied price options for a given product
 */
export function getProductPriceInfo(product: Product, productId: string, sizeId: string, restaurantId: string, inclusive: boolean, adjustments: Adjustments, menuId: string, now: Moment, booking: Booking | FunctionBooking, openingTimes: OpeningTimes, priceOptions: PriceOptions, specialProduct: ProductStock = null, adjustedProductPriceInMenu: number = null): ProductPriceInfo {
  const standardPrice = getStandardPrice(product, sizeId, restaurantId);
  const premiumPrice = getPremiumStandardPrice(product, productId, sizeId, restaurantId, inclusive, adjustments, menuId, now, booking, openingTimes, priceOptions);
  const info: ProductPriceInfo = {
    standardPrice: standardPrice,
    premiumStandardPrice: premiumPrice,
    finalPrice: inclusive ? 0 : standardPrice,
    appliedPriceOptions: [],
  };

  let finalPrice = standardPrice;
  if (inclusive) return info;
  if (!product?.restaurants?.[restaurantId]) return { ...info, finalPrice: 0 };
  else if (canThisBeNumber(specialProduct?.sizes?.[sizeId]?.price)) finalPrice = Number(specialProduct?.sizes?.[sizeId]?.price);
  else if (canThisBeNumber(adjustedProductPriceInMenu)) finalPrice = Number(adjustedProductPriceInMenu);

  const matchedAdjustments: Adjustment[] = getAdjustment(adjustments, menuId, now, booking, openingTimes);

  // Track which adjustments have been applied
  const appliedAdjustments = new Set<string>();
  const appliedPriceOptions: PriceOption[] = [];

  let overridePrice = null;
  matchedAdjustments.forEach((adjustment) => {
    const priceOption = priceOptions[adjustment.priceOptionId];
    if (isEmpty(priceOption)) return;

    if (appliedAdjustments.has(adjustment.priceOptionId)) return;

    const shouldApplyAdjustment = canApplyAdjustmentToProduct(productId, sizeId, priceOption);

    if (shouldApplyAdjustment && canThisBeNumber(priceOption.adjustmentProductIds?.[productId]?.[sizeId]?.overridePrice)) {
      overridePrice = priceOption.adjustmentProductIds[productId][sizeId].overridePrice;
      return;
    }

    if (shouldApplyAdjustment && priceOption) {
      appliedPriceOptions.push(priceOption);

      const diff = MoneyUtils.multiply(finalPrice, Math.abs(Number(priceOption.priceAdjustment) / 100));
      if (Number(priceOption.priceAdjustment) < 0) {
        finalPrice -= diff;
      } else {
        finalPrice += diff;
      }

      const nearest = priceOption.nearest ? Number(priceOption.nearest) : null;
      if (priceOption.rounding && nearest) {
        finalPrice = priceOption.rounding === "roundUp" ? Number(Math.round(finalPrice / nearest) * nearest) : Number(Math.floor(finalPrice / nearest) * nearest);
      } else if (priceOption.rounding) {
        finalPrice = priceOption.rounding === "roundUp" ? Math.round(finalPrice) : Math.floor(finalPrice);
      }

      appliedAdjustments.add(adjustment.priceOptionId);
    }
  });

  return {
    ...info,
    appliedPriceOptions,
    finalPrice: +Number(canThisBeNumber(overridePrice) ? overridePrice : finalPrice),
  };
}

/**
 * Returns price of given product with all adjustments and override applied.
 *
 * This function gets base price of the product then applies all applicable price adjustments
 * both +ve and -ve adjusments.
 * It also checks for overrides and returns final derived price with optional roundings.
 */
export function GetPrice(product: Product, productId: string, sizeId: string, restaurantId: string, inclusive: boolean, adjustments: Adjustments, menuId: string, now: Moment, booking: Booking | FunctionBooking, openingTimes: OpeningTimes, priceOptions: PriceOptions, specialProduct: ProductStock = null, adjustedProductPriceInMenu: number = null) {
  const priceInfo = getProductPriceInfo(product, productId, sizeId, restaurantId, inclusive, adjustments, menuId, now, booking, openingTimes, priceOptions, specialProduct, adjustedProductPriceInMenu);
  return priceInfo.finalPrice;
}

/**
 * Returns highest value out of Standard, Cumulative (with all increased adjustments applied over standard price) and highest Override prices.
 *
 * This does not apply negative adjustments like happy hour discounts.
 */
export function getPremiumStandardPrice(product: Product, productId: string, sizeId: string, restaurantId: string, inclusive: boolean, adjustments: Adjustments, menuId: string, now: Moment, booking: Booking | FunctionBooking, openingTimes: OpeningTimes, priceOptions: PriceOptions, specialProduct: SpecialProduct = null, adjustedProductPriceInMenu: number = null) {
  if (!product?.restaurants?.[restaurantId]) return 0;

  let derivedPrice = getStandardPrice(product, sizeId, restaurantId);
  const matchedAdjustments = getAdjustment(adjustments, menuId, now, booking, openingTimes);

  if (!matchedAdjustments.length) {
    return Number(derivedPrice);
  }

  // Track applied options to avoid duplicates
  let highestOverridePrice: number | null = null;
  const appliedPriceOptionIds = new Set<string>();

  // 1. Apply all positive price adjustments cumulatively
  matchedAdjustments.forEach((adjustment) => {
    const priceOption = priceOptions[adjustment.priceOptionId];
    const overridePrice = priceOption?.adjustmentProductIds?.[productId]?.[sizeId]?.overridePrice;
    if (overridePrice !== undefined) {
      highestOverridePrice = highestOverridePrice === null ? overridePrice : Math.max(highestOverridePrice, overridePrice);
    }

    if (!priceOption || appliedPriceOptionIds.has(adjustment.priceOptionId)) return;
    if (Number(priceOption.priceAdjustment) <= 0) return;
    let shouldApplyAdjustment = priceOption.applyToAllProducts ?? false;
    if (!priceOption.applyToAllProducts) {
      shouldApplyAdjustment = priceOption?.adjustmentProductIds?.[productId]?.[sizeId]?.enabled ?? false;
    } else if (priceOption?.adjustmentProductIds?.[productId]?.[sizeId]?.enabled) {
      return;
    }

    if (!shouldApplyAdjustment) return;

    const diff = derivedPrice * (Number(priceOption.priceAdjustment) / 100);
    derivedPrice += diff;

    const nearest = priceOption.nearest ? Number(priceOption.nearest) : null;
    if (priceOption.rounding && nearest) {
      derivedPrice = priceOption.rounding === "roundUp" ? Number(Math.round(derivedPrice / nearest) * nearest) : Number(Math.floor(derivedPrice / nearest) * nearest);
    } else if (priceOption.rounding) {
      derivedPrice = priceOption.rounding === "roundUp" ? Math.round(derivedPrice) : Math.floor(derivedPrice);
    }

    appliedPriceOptionIds.add(adjustment.priceOptionId);
  });

  // Return the higher of derived price or override price
  const finalPrice = highestOverridePrice !== null ? Math.max(derivedPrice, highestOverridePrice) : derivedPrice;

  return +Number(finalPrice);
}

export function getProductPrice(product: Product, restaurantId: string, sizeId: string, priceOption: PriceOption = null, adjustedProductPriceInMenu: number = null) {
  let standardPrice = canThisBeNumber(adjustedProductPriceInMenu) ? Number(adjustedProductPriceInMenu) : getStandardPrice(product, sizeId, restaurantId);
  if (!priceOption) return standardPrice;
  const nearest = priceOption.nearest ? Number(priceOption.nearest) : null;
  const diff = MoneyUtils.multiply(standardPrice, Math.abs(Number(priceOption.priceAdjustment) / 100));
  if (Number(priceOption.priceAdjustment) < 0) {
    standardPrice -= diff;
  } else {
    standardPrice += diff;
  }
  if (priceOption.rounding && nearest) {
    return priceOption.rounding === "roundUp" ? +Number(Math.round(standardPrice / nearest) * nearest) : +Number(Math.floor(standardPrice / nearest) * nearest);
  } else if (priceOption.rounding) {
    return priceOption.rounding === "roundUp" ? +Number(Math.round(standardPrice)) : +Number(Math.floor(standardPrice));
  }
  return +Number(standardPrice);
}

export function getProductPriceFormatted(product: Product, restaurantId, sizeId, inclusive: boolean, priceOption: PriceOption, adjustedProductPriceInMenu: number = null) {
  const price = getProductPrice(product, restaurantId, sizeId, priceOption, adjustedProductPriceInMenu);
  return MoneyUtils.format(price);
}

/**
 * Returns base restaurant price of the product.
 */
export function getStandardPrice(product: Product, sizeId = "", restaurantId: string) {
  // if (inclusive) return 0;
  const noPriceProductTypeIds = [
    ProductTypeId.inventory,
    ProductTypeId.cashout,
    ProductTypeId.contingentLiability,
    ProductTypeId.creditAccounts,
    ProductTypeId.customerServiceExpense,
    ProductTypeId.deposits,
    ProductTypeId.giftCertificates, //
    ProductTypeId.inventory,
    ProductTypeId.marketingPromotional,
    ProductTypeId.operationalExpense,
    ProductTypeId.pettyCashExpense,
  ];
  if (product && noPriceProductTypeIds.includes(product.typeId)) return null;

  if (!sizeId && product) {
    if ([ProductTypeId.fixedPriceMenu, ProductTypeId.depositMinSpend].includes(product.typeId) && product.fixedPriceMenuTypes) {
      sizeId = product.fixedPriceMenuTypes.adult;
    }
  }
  const price = product?.restaurants?.[restaurantId]?.price?.[sizeId];
  const standardPrice = sizeId && price !== undefined && price !== "" ? Number(price) : null;
  return standardPrice === null && product?.restaurants?.[restaurantId]?.price !== undefined ? Number(product.restaurants[restaurantId].price) : standardPrice;
}

export const getSizeIdsFromProduct = (product: Product) => {
  let sizeIds = [];
  if ([ProductTypeId.financial, ProductTypeId.beverage, ProductTypeId.combinationFoodAndBeverage, ProductTypeId.fixedPrice, ProductTypeId.inventory].includes(product.typeId)) {
    sizeIds = keys(pickBy(product.sizes, (v) => !!v));
  }
  return sizeIds;
};

/**
 * Sorts given GroupHeadingProductSizes based on ProductSizes order set in Office
 */
export const sortProductSizeIds = (ghProductSizes: GroupHeadingProductSizes, productSizes: ProductSize[] = []): ProductSize[] => {
  const ghProductSizeArray = map(ghProductSizes, (value, key) => ({
    id: key,
    order: value.order || 0,
    ...value,
  }));

  if (!isEmpty(productSizes) && ghProductSizeArray.length > 1) {
    const productSizeMap = keyBy(productSizes, "id") as { [id: string]: ProductSize };
    const sortedGHProductSizes = map(ghProductSizeArray, (size) => {
      const officeProductSize = (productSizeMap[size.id] || {}) as ProductSize;
      return {
        ...officeProductSize,
        enabled: size.enabled,
      };
    });
    return sortBy(sortedGHProductSizes, "order");
  } else {
    return sortBy(ghProductSizeArray, "order") as ProductSize[];
  }
};

export function groupHeadingToHierachy(groupHeadings: GroupHeadings, path: GroupHeadingHierachy[] = [], targetProductId = "", targetGroupHeadingId = ""): GroupHeadingHierachy[] {
  if (!targetProductId && !targetGroupHeadingId) return [];
  if (targetProductId && targetGroupHeadingId) throw new Error("Provide either ProductId or GroupHeadingId");
  for (const groupHeadingId in groupHeadings) {
    const currentHeading = groupHeadings[groupHeadingId];
    path.push({ groupHeadingId, name: currentHeading.displayName });
    // if it needs to check targetProductId
    if (targetProductId) {
      if (currentHeading.products?.[targetProductId]) {
        return path; // If the target node is found, return the recorded path.
      }
    }
    // if it needs to check targetGroupHeadingId
    else if (targetGroupHeadingId && targetGroupHeadingId === groupHeadingId) {
      return path; // If the target node is found, return the recorded path.
    }

    // Recursively search for the target node in the nested groupHeadings.
    const nestedPath = groupHeadingToHierachy(currentHeading.groupHeadings, path, targetProductId, targetGroupHeadingId);
    if (nestedPath.length) {
      return nestedPath; // Return the path if the target node is found in the nested groupHeadings.
    }
    path.pop(); // Backtrack if the target node is not found in the current groupHeadings.
  }
  return []; // Return undefined if the target node is not found in the current groupHeadings.
}

/**
 * Sorts OrderModifier products based on array index of Product.modifiers
 */
export const sortOrderModiferProducts = (orderModifiers: OrderModifiers, parentProduct: Product): OrderModifier[] => {
  if (!orderModifiers) return [];
  if (!parentProduct) return values(orderModifiers);
  return chain(parentProduct.modifiers)
    .pickBy((mod) => orderModifiers[mod.modifierGroupId] !== undefined)
    .map((productModifier) => orderModifiers[productModifier.modifierGroupId])
    .value();
};

/**
 * Sorts orderItemProducts by SortSequence of ComboGroupProduct in ComboGroup
 */
export const sortComboGroupProducts = (orderItemProducts: OrderItemProducts, comboGroupList: ComboGroup[]): Partial<OrderItem>[] => {
  if (!orderItemProducts) return [];
  if (!comboGroupList || comboGroupList.length === 0) {
    return keys(orderItemProducts).map((productId) => ({
      ...orderItemProducts[productId],
      productId,
    }));
  }

  return flatMap(comboGroupList, (combo) => {
    const sortedCombinations = orderBy(
      map(combo.combinations, (value, key) => ({ ...value, productId: key })),
      "sortSequence"
    );

    return compact(
      map(sortedCombinations, ({ productId, ...rest }) => {
        return has(orderItemProducts, productId) ? { ...orderItemProducts[productId], productId } : null;
      })
    );
  });
};

/**
 * Get product price by given type
 * type can be adult | child | string: eg sizeId)
 * @param restaurants
 * @param restaurantId
 * @param type
 * @returns number
 */
export const getProductPriceByType = (restaurants: { [restaurantId: string]: ProductInfo }, restaurantId: string, type?: "adult" | "child" | string): number => {
  const restaurantPrice = get(restaurants, [restaurantId, "price"]);
  let price;
  if (type) price = get(restaurantPrice, type || "adult");
  else price = restaurantPrice;

  return isNumber(price) || price ? Number(price) : price;
};

export const isProductGroupAndParentGroupEnabled = (productGroup: ProductGroup, productGroups: ProductGroups) => {
  if (!productGroup || !productGroup.enabled) return false;
  let parentGroupId = productGroup.parentGroup;
  while (parentGroupId !== "-1") {
    const parentGroup = productGroups[parentGroupId];
    if (!parentGroup || !parentGroup.enabled) return false;
    parentGroupId = parentGroup.parentGroup;
  }
  return true;
};

/**
 * Checks if given product matches given set of prduct filters
 * @param product
 * @param productFilters
 * @returns boolean
 */
export function matchesAppliedProductFilters(product: Product, productFilters: { [name: string]: string[] }) {
  const productFiltersTags = flatten(map(productFilters, (pf) => pf));

  const filterCheck = (product: Product, productFilters: { [name: string]: string[] }) => {
    const includeItems = [],
      excludeItems = [],
      includeItemsinUser = [];
    for (const filterGroupName in productFilters) {
      if (!(filterGroupName === "Allergies" || filterGroupName === "Ingredients of Concern")) {
        for (const j of productFilters[filterGroupName]) {
          includeItemsinUser.push(j);
        }
      }
    }
    for (const filterGroup in product?.tags) {
      for (const i in product.tags[filterGroup]) {
        if (filterGroup === "allergies" || filterGroup === "ingredientsOfConcern") {
          excludeItems.push(product.tags[filterGroup][i].value);
        } else {
          includeItems.push(product.tags[filterGroup][i].value);
        }
      }
    }

    if (intersection(excludeItems, flatten(map(productFilters, (pf) => pf))).length > 0) {
      return false;
    }
    return intersection(includeItems, includeItemsinUser).length === includeItemsinUser.length;
  };

  const productDisplay = filterCheck(product, productFilters);
  if ((productFiltersTags.length > 0 && !productDisplay) || !product) return false;
  return true;
}

export const flattenProductGroupHierarchy = (product: Product, delim = " > ") => {
  return product ? chain(product.productGroupHierachy).map("name").reverse().join(delim).value() : "";
};

export const groupProductsByGroupPath = (adjustmentProductIds: AdjustmentProductIds, allProducts: Product[]) =>
  chain(adjustmentProductIds)
    .reduce((grouped, searchResult, key) => {
      const product = find(allProducts, (p) => p.id === key);
      const pathString = flattenProductGroupHierarchy(product);

      grouped[pathString] = {
        [key]: searchResult,
      };

      return grouped;
    }, {})
    .thru((grouped) => chain(grouped).toPairs().sortBy([0]).fromPairs().value())
    .value();

/**
 * Transforms ProductSearchResult[] (from Algolia based ProductSearchs) into AdjustmentProductIds
 */
export const convertToAdjustedProductIds = (selectedProductIds: ProductSearchResult[], allProducts: Product[], existingAdjustmentProductIds: AdjustmentProductIds): AdjustmentProductIds => {
  if (isEmpty(allProducts)) return {};

  const filteredProducts = filter(allProducts, (p) => some(selectedProductIds, (s) => s.productId === p.id));
  const adjustmentProductIds: AdjustmentProductIds = {};

  filteredProducts.forEach((product) => {
    const { id, typeId, fixedPriceMenuTypes } = product;
    const selected = selectedProductIds.find((s) => s.productId === id);

    let sizeIds = keys(pickBy(selected?.sizes, (enabled) => !!enabled)).filter(Boolean);

    if (sizeIds.length === 0) {
      if ([ProductTypeId.fixedPriceMenu, ProductTypeId.depositMinSpend].includes(typeId) && fixedPriceMenuTypes?.adult) {
        sizeIds = [fixedPriceMenuTypes.adult];
      } else {
        const productSizeIds = keys(pickBy(product.sizes, (enabled) => !!enabled)).filter(Boolean);
        sizeIds = productSizeIds.length > 0 ? productSizeIds : ["noSize"];
      }
    }

    sizeIds.forEach((sizeId) => {
      adjustmentProductIds[id] = {
        ...existingAdjustmentProductIds?.[id],
        ...adjustmentProductIds[id],
        [sizeId]: {
          ...existingAdjustmentProductIds?.[id]?.[sizeId],
          enabled: true,
        },
      };
    });
  });

  return adjustmentProductIds;
};

/**
 * Transforms AdjustmentProductIds into ProductSearchResult[] (to initialise Algolia based ProductSearchs)
 */
export const convertToSearchResults = (adjustmentProductIds: AdjustmentProductIds, allProducts: Product[]): ProductSearchResult[] => {
  if (isEmpty(adjustmentProductIds) || isEmpty(allProducts)) {
    return [];
  }

  const productIds = keys(adjustmentProductIds);
  const filteredProducts = filter(allProducts, (p) => productIds.includes(p.id));

  return map(filteredProducts, (product) => {
    const adjustedSizes = adjustmentProductIds[product.id];
    const sizes: SizeIdMap<boolean> = {};

    forEach(keys(product.sizes), (sizeId) => {
      sizes[sizeId] = false;
    });

    forEach(entries(adjustedSizes), ([sizeId, option]) => {
      if (option.enabled) {
        sizes[sizeId] = true;
      }
    });

    return {
      ...product,
      productId: product.id,
      objectID: product.id,
      sizes,
    };
  });
};

export interface Group {
  productGroupId: string;
  count: number;
  productGroupName: string;
  productGroups: Group[];
}

export const getProductGroupIdsFromGroups = (allProductGroups: ProductGroup[], productGroups: ProductGroup[], visited: Set<string> = new Set()): string[] => {
  let productGroupIds: string[] = [];
  forEach(productGroups, (pg) => {
    // Guard against cyclic `parentGroup` data.so each group gets procesed once only
    if (visited.has(pg.id)) return;
    visited.add(pg.id);
    productGroupIds.push(pg.id);
    const children = filter(allProductGroups, (g) => g.parentGroup === pg.id);
    if (children.length) {
      productGroupIds = productGroupIds.concat(...getProductGroupIdsFromGroups(allProductGroups, children, visited));
    }
  });
  return productGroupIds;
};

export const productGroupsToGroups = (productGroups: ProductGroup[], products: Product[]): Group[] => {
  function recurse(productGroups1: ProductGroup[], visited: Set<string> = new Set()) {
    const groups: Group[] = [];
    forEach(productGroups1, (pg) => {
      // Same cyclic-data guard
      if (visited.has(pg.id)) return;
      visited.add(pg.id);
      const children = filter(productGroups, (g) => g.parentGroup === pg.id);
      const groupIds = getProductGroupIdsFromGroups(productGroups, children);
      const products1 = filter(products, (p) => p.groupId === pg.id || some(groupIds, (id) => p.groupId === id));
      if (products1.length) {
        const newGroup: Group = {
          productGroupId: pg.id,
          count: products1.length,
          productGroupName: pg.name,
          productGroups: recurse(children, visited),
        };
        groups.push(newGroup);
      }
    });
    return groups;
  }
  return recurse(filter(productGroups, (g) => g.parentGroup === "-1"));
};

export const getProductGroups = (productGroups: Group[], productGroupId: string): Group[] => {
  const cumulator: Group[] = [];
  function recurse(newGroupId: string): Group[] {
    const groups: Group[] = filter(productGroups, (g) => g.productGroupId === newGroupId);
    forEach(groups, (g) => {
      const newGroup: Group = {
        productGroupId: g.productGroupId,
        count: 0,
        productGroupName: g?.productGroupName,
        productGroups: recurse(g.productGroupId),
      };
      cumulator.push(newGroup);
    });
    return cumulator;
  }
  recurse(productGroupId);
  return cumulator;
};

export const getProductGroupChildren = (productGroups: Group[], groupId): Group[] => {
  const result: Group[] = [];
  const nestedGroups = getProductGroups(productGroups, groupId);
  const getGroupsRecursive = (nestedGroups1: Group[]) => {
    if (nestedGroups1.length === 0) return [];
    nestedGroups1.map((group) => {
      result.push(group);
    });
    return result;
  };
  return getGroupsRecursive(nestedGroups);
};

export const filterEnabledProducts = (productGroupId: string, restaurantId: string, products: Product[]): Product[][] => {
  const accountProducts: Product[][] = chunk(
    filter(products, (p: Product) => p.groupId === productGroupId && p.enabled && p.restaurants && (p.restaurants?.[restaurantId]?.enabled || ![ProductTypeId.financial, ProductTypeId.food, ProductTypeId.beverage, ProductTypeId.fixedPriceMenu, ProductTypeId.combinationFoodAndBeverage, ProductTypeId.extendDuration, ProductTypeId.tableClass, ProductTypeId.fixedPrice].includes(p.typeId))),
    6
  );
  return accountProducts;
};

export const getAveragePrice = (voucherProducts: VoucherProduct[], products: Product[], restaurants: string[]) => {
  if (isEmpty(voucherProducts)) return null;

  const validPrices = chain(voucherProducts)
    .flatMap((voucherProduct) => {
      const product = find(products, { id: voucherProduct.productId });
      const productSizes = keys(voucherProduct.sizes);

      return flatMap(restaurants, (restaurant) => productSizes.map((size) => get(product, `restaurants.${restaurant}.price.${size}`)));
    })
    .compact()
    .map(parseFloat)
    .compact()
    .value();

  return isEmpty(validPrices) ? null : mean(validPrices);
};

export function combineGroupsWithProducts(products: Products, productGroups: ProductGroups, restaurantId: string, productGroupTree: TreeData[]) {
  const attachRecursive = (groups: TreeData[]): TreeData[] => {
    return map(groups, (group, i) => {
      const productsByRestaurant = getProductsByGroupId(products, productGroups, group.id, restaurantId);
      const productList = map(productsByRestaurant, (p, id) => ({ ...p, id } as Product));
      // find products that belong to this group
      const groupProducts = chain(productList)
        .filter((p) => p.groupId === group.id)
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0) || (a.name ?? "").localeCompare(b.name ?? "") || (a.id ?? "").localeCompare(b.id ?? ""))
        .map((p) => ({
          id: p.id,
          name: p.name,
          isGroup: false,
          parentId: group.id,
          sequence: p.sequence ?? i,
        }))
        .value();

      const childGroups = attachRecursive(group.children);

      // Products must only ever live at the bottom of the tree. If a group has both child
      // groups and direct products, show both so the offending groups can be flagged.
      const hasProductSiblings = groupProducts.length > 0 && childGroups.length > 0;

      return {
        ...group,
        children: hasProductSiblings ? [...map(childGroups, (g) => ({ ...g, hasProductSiblings: true })), ...groupProducts] : groupProducts.length > 0 ? groupProducts : childGroups, // 👈 recurse down
      };
    });
  };

  return attachRecursive(productGroupTree);
}

export function getProductsByGroupId(products: Products, productGroups: ProductGroups, productGroupId: string, restaurantId = ""): Products {
  let productsByGroupId: Products = {};
  const groupIdsToProcess: string[] = [productGroupId];
  const processedGroupIds: Set<string> = new Set();

  while (groupIdsToProcess.length > 0) {
    const currentGroupId = groupIdsToProcess.shift()!;

    if (processedGroupIds.has(currentGroupId)) continue;
    processedGroupIds.add(currentGroupId);
    productsByGroupId = {
      ...productsByGroupId,
      ...pickBy(products, (product) => product.groupId === currentGroupId && productGroups[product.groupId] && (restaurantId === "" || (product.restaurants && product.restaurants[restaurantId]?.enabled === true))),
    };

    const nestedProductGroups = pickBy(productGroups, (pg) => {
      if (pg.id === pg.parentGroup) console.error(`product group cannot be its own parent ${pg.id} ${pg.name}`);
      return pg.parentGroup === currentGroupId;
    });
    groupIdsToProcess.push(...keys(nestedProductGroups));
  }

  return productsByGroupId;
}

export const normalizedProductGroup = (data: ProductGroups): TreeData[] => {
  // Recursive helper: build children for a given parentId
  const buildTree = (data: ProductGroups, parentId: string | null = null): TreeData[] => {
    return Object.entries(data)
      .filter(([_, value]) => (value.parentGroup ?? null) === parentId)
      .map(([id, value], index) => ({
        id,
        name: value.name,
        parentId,
        isGroup: true,
        isSpecial: value.systemGroup === true,
        sequence: value.sequence ?? index,
        children: buildTree(data, id), // 👈 recursive step
      }))
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0) || (a.name ?? "").localeCompare(b.name ?? "") || (a.id ?? "").localeCompare(b.id ?? ""));
  };

  return buildTree(data, "-1"); // start from root nodes
};
export const isOpenProduct = (product: Product, restaurantId: string) => {
  return !isEmpty(product) && product.isOpenPriceProduct && product.restaurants?.[restaurantId]?.enabled;
};

export const isNoSaleProduct = (product: Product, restaurantId: string, sizeId: string) => {
  return !isEmpty(product) && product.restaurants?.[restaurantId]?.enabled && !!product.restaurants?.[restaurantId]?.noSaleItem?.[sizeId];
};

export const getProductGroupHierachy = (productGroups: ProductGroups, product: Product): ProductGroupHierachy[] => {
  const productGroupHierachy: ProductGroupHierachy[] = [];

  let productGroup: ProductGroup;
  let parentGroupId: string = product.groupId;

  while (parentGroupId != "-1") {
    productGroup = productGroups[parentGroupId];

    if (!productGroup) {
      break;
    }

    const hierachy: ProductGroupHierachy = {
      productGroupId: parentGroupId,
      name: productGroup.name,
      productCategoryId: productGroup.productCategoryId,
    };
    parentGroupId = productGroup?.parentGroup || "-1";
    productGroupHierachy.push(hierachy);
  }
  return productGroupHierachy;
};

export const getProductParentGroupIds = (groups: ProductGroups, groupId: string): string[] => {
  const allParentGroupIds: string[] = [groupId];
  let current = groups[groupId];

  while (current?.parentGroup && current.parentGroup !== "-1") {
    allParentGroupIds.push(current.parentGroup);
    current = groups[current.parentGroup];
  }

  return allParentGroupIds;
};

/**
 * Build group lookup maps with products
 */
export function getProductEnchancementGroups(additionGroups: AdditionGroups, modifierGroups: ModifierGroups, upsellGroups: UpsellGroups, preparationGroups: PreparationGroups, products: Product[]) {
  const groupMaps = {
    additions: additionGroups,
    modifiers: modifierGroups,
    upsells: upsellGroups,
    preparations: preparationGroups,
  };

  const buildGroupWithProducts = (group: any) => ({
    ...group,
    productsMetaData: filter(map(group.products || [], (product) => find(products, { id: product.productId }))),
  });

  return mapValues(groupMaps, (groups) => mapValues(groups, buildGroupWithProducts));
}

const replaceSizeIdsWithNames = (obj: any, productSizes: ProductSizes): any => {
  if (isNil(obj) || !isObject(obj)) return obj;
  if (isArray(obj)) return map(obj, (item) => replaceSizeIdsWithNames(item, productSizes));

  const objKeys = keys(obj);
  const sizeKeys = filter(objKeys, (key) => key in productSizes);

  // Transform size ID keys to size names
  if (sizeKeys.length > 0) {
    return reduce(
      obj,
      (result, value, key) => {
        const sizeName = productSizes[key]?.name || key;
        result[sizeName] = isObject(value) && !isArray(value) ? replaceSizeIdsWithNames(value, productSizes) : value;
        return result;
      },
      {} as any
    );
  }

  return mapValues(obj, (value) => replaceSizeIdsWithNames(value, productSizes));
};

/**
 * Enrich products with their group metadata
 */
export const getEnrichedProducts = (additionGroups: AdditionGroups, modifierGroups: ModifierGroups, upsellGroups: UpsellGroups, preparationGroups: PreparationGroups, products: Product[], productGroups: ProductGroups, productSizes: ProductSizes): EnrichedProduct[] => {
  const enhancers = getProductEnchancementGroups(additionGroups, modifierGroups, upsellGroups, preparationGroups, products);

  const groupIdKeys: Record<ProductEnhancementType, string> = {
    additions: "additionGroupId",
    modifiers: "modifierGroupId",
    upsells: "upsellGroupId",
    preparations: "preparationGroupId",
  };

  const enrichProduct = (product: Product): EnrichedProduct => {
    const enrichedMetaData: EnrichedProductMetaData = {
      groupId: productGroups?.[product?.groupId]?.name || "",
      typeId: findKey(ProductTypeId, (v) => v === product?.typeId) || "",
      productCategoryId: findKey(ProductCategoryId, (v) => v === product?.productCategoryId) || "",
      sizes: replaceSizeIdsWithNames(product.sizes, productSizes),
      restaurants: replaceSizeIdsWithNames(product.restaurants, productSizes),
    };

    // Enrich enhancement types (additions, modifiers, upsells, preparations)
    (Object.keys(groupIdKeys) as ProductEnhancementType[]).forEach((type) => {
      const items = product[type];
      if (!items?.length) return;

      (enrichedMetaData as any)[type] = map(items, (item) => {
        if (!item || typeof item !== "object") return item;

        const groupId = item[groupIdKeys[type]];
        const group = enhancers[type]?.[groupId];
        if (!group) return item;

        return {
          ...item,
          metadata: {
            ...group,
            products: map(
              filter(group.products, (p) => p.id !== product.id),
              enrichProduct
            ),
          },
        };
      });
    });

    // Enrich combo products
    if (!isEmpty(product.groupProducts)) {
      enrichedMetaData.groupProducts = mapValues(product.groupProducts, (comboGroup, comboSizeId) =>
        map(comboGroup, (cg) => ({
          ...cg,
          metaData: compact(map(keys(cg.combinations), (comboProductId) => find(products, { id: comboProductId }))),
        }))
      );

      // Replace size IDs with names in keys
      enrichedMetaData.groupProducts = mapKeys(enrichedMetaData.groupProducts, (_, sizeId) => productSizes?.[sizeId]?.name || sizeId);
    }

    const baseProduct = product?.name && product?.docketName ? product : find(products, (p) => p.id === (product as any).productId || p.id === (product as any).id);

    return { ...baseProduct, metaData: enrichedMetaData } as EnrichedProduct;
  };

  return compact(map(products, enrichProduct));
};

export const getFoodTolerancesSelectOptions = (tag: keyof FoodIntolerances): Array<SelectOption & { abbreviation: string }> => {
  const data = get(foodIntolerances, tag);
  if (!data) return [];
  const results = [];

  data.forEach((item) => {
    if ("name" in item) {
      const option = {
        value: item.name,
        label: "abbreviation" in item && item.abbreviation ? `${item.name} (${item.abbreviation})` : item.name,
        abbreviation: item?.abbreviation ?? "",
      };
      results.push(option);
    } else {
      // If nested obj then also add option groups
      keys(item).forEach((groupKey) => {
        const groupItems = item[groupKey as keyof typeof item] as FoodIntolerance[];
        const options = groupItems.map((groupItem) => ({
          value: groupItem.name,
          label: "abbreviation" in groupItem && groupItem?.abbreviation ? `${groupItem.name} (${groupItem.abbreviation})` : groupItem.name,
          abbreviation: item?.abbreviation ?? "",
        }));

        results.push({
          label: groupKey,
          options,
        });
      });
    }
  });

  return results;
};

// moved from resbutler/core/productUtils.ts
const ACCOUNT_MAPPINGS = [ProductCategoryId.CreditAccounts, ProductCategoryId.CustomerServiceExpense, ProductCategoryId.OperationalExpense, ProductCategoryId.Cashout, ProductCategoryId.MarketingPromotional, ProductCategoryId.PrepaidMenus];

export function getAccountMappings(products: Product[]): Product[] {
  return filter(products, (p) => includes(ACCOUNT_MAPPINGS, p.productCategoryId));
}

export const getAmountAfterRoundingFromPriceOption = (priceOption: PriceOption, amount: number) => {
  const nearest = Number(priceOption.nearest);
  if (!priceOption.rounding || !priceOption.nearest) return Math.round(amount);
  return priceOption.rounding === "roundUp" ? Math.round(amount / nearest) * nearest : Math.floor(amount / nearest) * nearest;
};

const getPriceAfterPriceOption = (amount: number, priceOption: PriceOption) => {
  const percentAmount = Math.round(amount * (Number(priceOption.priceAdjustment) / 100));
  return getAmountAfterRoundingFromPriceOption(priceOption, percentAmount);
};

export const calculateSurcharges = ({ lineItems, nonSurchargeableAmount = 0, additionalSurchargeableAmount = 0, surcharges1, serviceCharge1, taxRate }: { lineItems: LineItem[]; nonSurchargeableAmount?: number; additionalSurchargeableAmount?: number; surcharges1: ServiceSurcharge[]; serviceCharge1: ServiceSurcharge; taxRate: number }) => {
  const surcharges: TransactionSurcharge[] = [];
  let serviceCharge: TransactionSurcharge = null;

  const lineTotals = map(lineItems, (li) => li.total);

  // Split an amount across line items proportionally to their totals (same order as lineItems)
  const splitAcrossLineItems = (amount: number) => (lineItems.length && amount > 0 ? MoneyUtils.splitProportional(amount, lineTotals) : map(lineItems, () => 0));

  const deductions = splitAcrossLineItems(nonSurchargeableAmount);
  const additions = splitAcrossLineItems(additionalSurchargeableAmount);

  // Each line item's surchargeable amount: total - its non-surchargeable share + its additional share
  const netLineItems = map(lineItems, (li, i) => ({
    li,
    net: Math.max(0, li.total - (deductions[i] ?? 0) + (additions[i] ?? 0)),
  }));

  forEach(
    filter(surcharges1, (s) => !!s),
    (surcharge) => {
      const priceOption = surcharge.priceOption;
      if (Number(priceOption.priceAdjustment) !== 0) {
        const filtered = filter(netLineItems, ({ li }) => canApplyAdjustmentToProduct(getLineItemProductId(li), getLineItemSizeId(li), priceOption));
        if (filtered.length > 0 || !!surcharge.isManuallyApplied) {
          const totalAmount = sumBy(filtered, ({ net }) => net);
          const value = getPriceAfterPriceOption(totalAmount, priceOption);
          surcharges.push({
            _key: crypto.randomUUID(),
            id: surcharge.id,
            name: surcharge.name,
            priceOptionId: surcharge.priceOptionId,
            value,
            salesTax: getTaxAmount(taxRate, value),
            priceOption,
            isManuallyApplied: surcharge.isManuallyApplied || false,
          });
        }
      }
    }
  );

  const totalSurchargesAmount = surcharges.reduce((sum, cur) => sum + cur.value, 0);

  if (serviceCharge1) {
    const priceOption = serviceCharge1.priceOption;
    if (Number(priceOption.priceAdjustment) !== 0) {
      const filtered = filter(netLineItems, ({ li }) => canApplyAdjustmentToProduct(getLineItemProductId(li), getLineItemSizeId(li), priceOption));
      if (filtered.length > 0 || !!serviceCharge1.isManuallyApplied) {
        const totalAmount = sumBy(filtered, ({ net }) => net);
        const amount = totalAmount + totalSurchargesAmount;
        const value = getPriceAfterPriceOption(amount, priceOption);
        serviceCharge = {
          _key: crypto.randomUUID(),
          id: serviceCharge1.id,
          name: serviceCharge1.name,
          priceOptionId: serviceCharge1.priceOptionId,
          value,
          salesTax: getTaxAmount(taxRate, value),
          priceOption,
          isManuallyApplied: serviceCharge1.isManuallyApplied || false,
        };
      }
    }
  }

  return { surcharges, serviceCharge };
};

export function getProductMenuTotal(menu: Menu, product: Product, booking: Booking, pax: number, childPax: number) {
  const menuItem = getMenuItem(product, booking.restaurantId, 1, menu?.menuPayments?.[booking.courseId]?.fixed?.priceReleaseSchedules, booking.date, booking.intervalId);
  const adultPrice = !menuItem.isChild ? menuItem.amount : 0; //product.restaurants[restaurantId].price["adult"];
  const childPrice = menuItem.isChild ? menuItem.amount : 0; //product.restaurants[restaurantId].price["child"];
  if (!pax && !childPax) return { adultPrice, childPrice, total: 0 };
  let total = 0;
  if (menu.menuPayments?.[booking.courseId].paymentRequirementTypeId === PaymentRequirementTypes.FullPayment || menu.menuPayments?.[booking.courseId].paymentRequirementTypeId === PaymentRequirementTypes.Deposit) {
    if (product.restaurants[booking.restaurantId].price["adult"]) {
      total += Number(adultPrice || 0) * pax;
    }
    if (product.restaurants[booking.restaurantId].price["child"]) {
      total += Number(childPrice || 0) * (childPax || 0);
    }
  }
  return { adultPrice, childPrice, total };
}
